import { z } from "zod";
import type { Operation } from "../types";

export const searchHitSchema = z.object({
  id: z.string().min(1),
  href: z.string().min(1),
  label: z.string().min(1),
  hint: z.string().min(1).optional(),
  group: z.string().min(1),
  /** Optional freshness stamp. Stale hits are dropped by WP8c. */
  updatedAt: z.string().min(1).optional(),
  score: z.number().optional(),
});
export type SearchHit = z.infer<typeof searchHitSchema>;

export const shellSearchResponseSchema = z.object({
  hits: z.array(searchHitSchema),
  environment: z.enum(["demo", "real"]),
  limit: z.number().int().positive(),
  asOf: z.string().min(1),
});
export type ShellSearchResponse = z.infer<typeof shellSearchResponseSchema>;

export interface ProjectsSearchResponse {
  projects: Array<{ slug: string; name: string }>;
  tasks: Array<{
    id: number;
    title: string;
    project_slug: string;
    project_name: string;
    section_slug: string;
    section_name: string;
  }>;
}

// `scope` is the current page's search context (e.g. "personal" on /personal).
// When set, the route runs that scope's own search and returns those hits first,
// so the dock searches "the page you're on" before the rest of the app
// (docs/personal/SCOPED-SEARCH-PRD.md).
const searchQuery = z.object({
  q: z.string().optional(),
  scope: z.string().optional(),
  env: z.enum(["demo", "real"]).optional(),
  actorId: z.string().min(1).max(200).optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
export type SearchQuery = z.infer<typeof searchQuery>;

const shellGetOp: Operation<void, SearchQuery, void, ShellSearchResponse> = {
  method: "GET",
  path: () => "/api/search",
  query: searchQuery,
  response: shellSearchResponseSchema,
};

const projectsGetOp: Operation<void, void, void, ProjectsSearchResponse> = {
  method: "GET",
  path: () => "/projects/api/search",
  response: z.custom<ProjectsSearchResponse>(),
};

export const search = {
  shellGet: shellGetOp,
  projectsGet: projectsGetOp,
};
